import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

/* ═══════════════════════════════════════════════════════════════════════
   LUMINEX — THE PROCESS EXTRACTION CANVAS
   "The Extraction Moment": unstructured chaos → cognitive lens → structure
   Pure SVG + CSS keyframes. No Framer Motion. No WebGL.
   ═══════════════════════════════════════════════════════════════════════ */

/* Canvas viewBox: 640 × 540
   Chaos zone:      x   0 → 225   (35%)
   Lens zone:       x 225 → 355   (20%)
   Structure zone:  x 355 → 640   (45%)                                  */

/* ───────── FRAGMENT CONTENT POOLS ───────── */
const FRAGMENT_POOL = [
  { type: "doc",    text: "…step 3: validate with finance team before…",             cat: "TASK"      },
  { type: "doc",    text: "…all capex above threshold requires dual approval…",       cat: "CONSTRAINT"},
  { type: "doc",    text: "…if vendor not on AVL, initiate onboarding per SOP-214…",  cat: "TASK"      },
  { type: "doc",    text: "…supporting documents must be attached within 48 hours…",  cat: "ARTIFACT"  },
  { type: "doc",    text: "…exceptions handled case-by-case by department head…",     cat: "ACTOR"     },
  { type: "quote",  text: "we usually skip that because legal takes too long…",      cat: "HANDOFF"   },
  { type: "quote",  text: "honestly, no one really follows that step anymore…",       cat: "TASK"      },
  { type: "quote",  text: "Rahul knows the real process, ask him…",                    cat: "ACTOR"     },
  { type: "quote",  text: "there's an unwritten rule about weekend approvals…",       cat: "CONSTRAINT"},
  { type: "slack",  text: "@maya I think this process is wrong…",                     cat: "GATEWAY"   },
  { type: "slack",  text: "anyone know who owns the vendor check step?",              cat: "ACTOR"     },
  { type: "slack",  text: "FYI this is the third time this week…",                     cat: "GATEWAY"   },
  { type: "slack",  text: "can we just bypass the usual flow for this one?",           cat: "HANDOFF"   },
  { type: "email",  text: "Re: Re: FWD: Purchase approval flow",                       cat: "TASK"      },
  { type: "email",  text: "ACTION REQUIRED: invoice #847 stuck in…",                   cat: "ARTIFACT"  },
  { type: "email",  text: "Re: escalation on PO-2934 pending since…",                  cat: "HANDOFF"   },
  { type: "log",    text: "2024-11-14 | WF-847 | escalated | reason: unknown",         cat: "GATEWAY"   },
  { type: "log",    text: "2024-11-15 | PO-2934 | timeout | no_owner_assigned",        cat: "ACTOR"     },
  { type: "log",    text: "2024-11-15 | REQ-1847 | bypassed | override_by: admin",     cat: "CONSTRAINT"},
  { type: "log",    text: "2024-11-16 | APR-0482 | loop_detected | cycle_count: 3",    cat: "GATEWAY"   },
  { type: "policy", text: "All purchase orders above ₹5L require dual sign-off…",     cat: "CONSTRAINT"},
  { type: "policy", text: "Vendor onboarding must include ESG screening…",             cat: "CONSTRAINT"},
  { type: "policy", text: "Claim filings subject to 14-day contractual window…",       cat: "CONSTRAINT"},
  { type: "meeting",text: "Action: John to verify compliance before…",                 cat: "TASK"      },
  { type: "meeting",text: "Decision: proceed without legal review given urgency…",     cat: "GATEWAY"   },
  { type: "meeting",text: "Next step: escalate to steering committee…",                cat: "HANDOFF"   },
  { type: "tribal", text: "We always ask Rahul first because he's been here 20 years…",cat: "ACTOR"     },
  { type: "tribal", text: "The real approval happens informally over tea…",             cat: "TASK"      },
  { type: "tribal", text: "Month-end exceptions are handled differently…",             cat: "GATEWAY"   },
];

/* ───────── GRAPH NODE CONTENT POOLS ───────── */
const ACTIVITY_LABELS = [
  "Review Invoice", "Verify Vendor", "Approve Request", "Send Notification",
  "Escalate to Manager", "Generate PO", "Receive Goods", "Match 3-Way",
  "Process Payment", "Archive Record", "Check Compliance", "Validate Budget",
  "Draft Contract", "Initiate Audit",
];
const ACTOR_LABELS = [
  "FINANCE", "LEGAL", "OPS", "PROCUREMENT", "TREASURY",
  "AUDIT", "COMMERCIAL", "HSE", "QUALITY", "ENG",
];
const ARTIFACT_LABELS = ["PO", "INV", "MSA", "RCPT", "RPT", "CERT", "MEMO"];
const CONSTRAINT_LABELS = ["SLA", "POLICY", "DEADLINE", "THRESHOLD", "REG"];

const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
const uuid = () => Math.random().toString(36).slice(2, 10);

/* ───────── FRAGMENT TYPE ICON (tiny SVG glyph) ───────── */
function FragmentGlyph({ type, cx, cy }) {
  const color = "rgba(154,162,177,0.85)";
  switch (type) {
    case "doc":
      return <path d={`M${cx-3},${cy-3.5} h4.5 l2,2 v5 h-6.5 z M${cx+1.5},${cy-3.5} v2 h2`} fill="none" stroke={color} strokeWidth="0.6" />;
    case "quote":
      return <text x={cx} y={cy+2.5} textAnchor="middle" fontFamily="Georgia,serif" fontSize="10" fill={color} fontStyle="italic">❝</text>;
    case "slack":
      return <text x={cx} y={cy+2.5} textAnchor="middle" fontFamily="JetBrains Mono,monospace" fontSize="9" fill={color} fontWeight="700">@</text>;
    case "email":
      return <g fill="none" stroke={color} strokeWidth="0.6"><rect x={cx-3.5} y={cy-2} width="7" height="5" /><polyline points={`${cx-3.5},${cy-2} ${cx},${cy+1.5} ${cx+3.5},${cy-2}`} /></g>;
    case "log":
      return <text x={cx} y={cy+2.5} textAnchor="middle" fontFamily="JetBrains Mono,monospace" fontSize="7" fill={color} fontWeight="700">&gt;_</text>;
    case "policy":
      return <g fill="none" stroke={color} strokeWidth="0.6"><path d={`M${cx-3},${cy-3} l3,-1 l3,1 v3 q0,2 -3,3 q-3,-1 -3,-3 z`} /></g>;
    case "meeting":
      return <g stroke={color} strokeWidth="0.6" fill="none"><line x1={cx-3} y1={cy-2.5} x2={cx+3} y2={cy-2.5} /><line x1={cx-3} y1={cy-0.5} x2={cx+3} y2={cy-0.5} /><line x1={cx-3} y1={cy+1.5} x2={cx+1.5} y2={cy+1.5} /></g>;
    case "tribal":
    default:
      return <path d={`M${cx-3.5},${cy-2} h7 v3.5 h-4 l-2,2 v-2 h-1 z`} fill="none" stroke={color} strokeWidth="0.6" />;
  }
}

/* ───────── INITIAL GRAPH SEED ───────── */
const SEED_NODES = [
  { id: "n1", kind: "actor",    label: "PROCUREMENT",    x: 438, y:  96 },
  { id: "n2", kind: "activity", label: "Verify Vendor",  x: 518, y:  92 },
  { id: "n3", kind: "gateway",  label: "?",              x: 466, y: 160 },
  { id: "n4", kind: "activity", label: "Generate PO",    x: 548, y: 162 },
  { id: "n5", kind: "artifact", label: "PO",             x: 605, y: 198 },
  { id: "n6", kind: "activity", label: "Match 3-Way",    x: 478, y: 232 },
  { id: "n7", kind: "constraint", label: "SLA",          x: 542, y: 260 },
  { id: "n8", kind: "actor",    label: "FINANCE",        x: 398, y: 280 },
  { id: "n9", kind: "activity", label: "Approve Request",x: 460, y: 330 },
  { id: "n10", kind: "gateway", label: "?",              x: 540, y: 330 },
  { id: "n11", kind: "activity", label: "Process Payment",x: 590, y: 380 },
  { id: "n12", kind: "actor",    label: "TREASURY",      x: 500, y: 420 },
  { id: "n13", kind: "constraint", label: "POLICY",      x: 410, y: 400 },
  { id: "n14", kind: "artifact", label: "RCPT",          x: 616, y: 435 },
];

const SEED_EDGES = [
  ["n1","n2"], ["n2","n3"], ["n3","n4"], ["n4","n5"],
  ["n3","n6"], ["n6","n7"], ["n6","n8"], ["n8","n9"],
  ["n9","n10"], ["n10","n11"], ["n11","n14"], ["n11","n12"],
  ["n9","n13"], ["n5","n6"],
];

/* ───────── AMBIENT PARTICLES (pre-computed, CSS-only) ───────── */
const AMBIENT_PARTICLES = Array.from({ length: 46 }, (_, i) => ({
  id: i,
  x: 10 + Math.random() * 210,
  y: 30 + Math.random() * 490,
  delay: Math.random() * 8,
  dur: 14 + Math.random() * 10,
}));

/* ───────── COMPONENT ───────── */
export default function LuminexExtractionCanvas() {
  const [fragments, setFragments] = useState([]);
  const [packets, setPackets] = useState([]);
  const [nodes, setNodes] = useState(SEED_NODES);
  const [edges, setEdges] = useState(SEED_EDGES);
  const [pulseNode, setPulseNode] = useState(null);
  const [catToken, setCatToken] = useState(null); // {x,y,label,id}
  const [metrics, setMetrics] = useState({ sources: 847, parsed: 4247, patterns: 847, rate: 142, nodes: 1247, edges: 4891 });
  const [reduceMotion, setReduceMotion] = useState(false);

  const fragIdRef = useRef(0);
  const pktIdRef = useRef(0);

  /* reduced-motion detection */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const fn = (e) => setReduceMotion(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  /* fragment spawner */
  useEffect(() => {
    if (reduceMotion) return;
    let alive = true;
    const spawn = () => {
      if (!alive) return;
      const pool = FRAGMENT_POOL[Math.floor(Math.random() * FRAGMENT_POOL.length)];
      const y = 40 + Math.random() * 460;
      const dur = 28 + Math.random() * 22; // seconds to traverse
      const rot = (Math.random() * 6 - 3).toFixed(2);
      const id = ++fragIdRef.current;
      setFragments((prev) => [...prev.slice(-18), { id, ...pool, y, dur, rot, born: Date.now() }]);
      // auto-remove after duration
      setTimeout(() => {
        setFragments((prev) => prev.filter((f) => f.id !== id));
      }, dur * 1000 + 500);
      // next spawn
      setTimeout(spawn, 1400 + Math.random() * 1800);
    };
    // initial stagger
    for (let i = 0; i < 8; i++) setTimeout(spawn, i * 1200);
    return () => { alive = false; };
  }, [reduceMotion]);

  /* scan line: emit packets when scan crosses lens mid-point.
     We approximate by timed packet emission (every 2.2-3.5s) with
     a y value drawn from currently visible fragments nearest the
     lens (x > 140).                                                  */
  useEffect(() => {
    if (reduceMotion) return;
    let alive = true;
    const tick = () => {
      if (!alive) return;
      setFragments((prev) => {
        // choose a fragment currently near the lens
        const now = Date.now();
        const candidates = prev.filter((f) => {
          const elapsed = (now - f.born) / 1000;
          const progress = elapsed / f.dur;
          return progress > 0.55 && progress < 0.9;
        });
        if (candidates.length > 0) {
          const frag = candidates[Math.floor(Math.random() * candidates.length)];
          const tokenId = uuid();
          setCatToken({ id: tokenId, x: 236, y: frag.y, label: `→ ${frag.cat}` });
          setTimeout(() => setCatToken((c) => (c && c.id === tokenId ? null : c)), 700);
          // emit packet
          const pkt = { id: ++pktIdRef.current, fromY: frag.y, targetId: null, born: Date.now() };
          // choose: 35% new node, else reinforce existing
          setNodes((curNodes) => {
            const newNode = Math.random() < 0.32;
            if (newNode && curNodes.length < 17) {
              const kinds = ["activity","activity","activity","actor","gateway","artifact","constraint"];
              const kind = rand(kinds);
              const label = kind === "activity" ? rand(ACTIVITY_LABELS)
                : kind === "actor" ? rand(ACTOR_LABELS)
                : kind === "gateway" ? "?"
                : kind === "artifact" ? rand(ARTIFACT_LABELS)
                : rand(CONSTRAINT_LABELS);
              const nx = 385 + Math.random() * 230;
              const ny = 80 + Math.random() * 380;
              const id = "n" + uuid();
              pkt.targetId = id;
              pkt.targetX = nx;
              pkt.targetY = ny;
              pkt.newNode = { id, kind, label, x: nx, y: ny, justBorn: true };
              return curNodes;
            } else {
              const target = curNodes[Math.floor(Math.random() * curNodes.length)];
              pkt.targetId = target.id;
              pkt.targetX = target.x;
              pkt.targetY = target.y;
              return curNodes;
            }
          });
          setPackets((p) => [...p.slice(-8), pkt]);
          // on packet arrival: materialize node or pulse
          setTimeout(() => {
            if (pkt.newNode) {
              setNodes((curNodes) => {
                if (curNodes.length >= 18) return curNodes;
                // add near packet target
                const newEdges = [];
                // connect to 1-2 nearest nodes
                const dists = curNodes.map((n) => ({ id: n.id, d: Math.hypot(n.x - pkt.newNode.x, n.y - pkt.newNode.y) }))
                  .sort((a,b) => a.d - b.d).slice(0, 2);
                dists.forEach((d) => newEdges.push([d.id, pkt.newNode.id]));
                setEdges((curE) => [...curE, ...newEdges]);
                return [...curNodes, pkt.newNode];
              });
            } else {
              setPulseNode(pkt.targetId);
              setTimeout(() => setPulseNode(null), 260);
            }
            setPackets((p) => p.filter((pp) => pp.id !== pkt.id));
          }, 1200);
        }
        return prev;
      });
      setTimeout(tick, 2100 + Math.random() * 1400);
    };
    setTimeout(tick, 1800);
    return () => { alive = false; };
  }, [reduceMotion]);

  /* periodic node replacement — every ~14s retire an old node */
  useEffect(() => {
    if (reduceMotion) return;
    const itv = setInterval(() => {
      setNodes((curNodes) => {
        if (curNodes.length <= 12) return curNodes;
        // retire an old seed-ish node
        const candidates = curNodes.filter((n) => !n.justBorn);
        if (candidates.length === 0) return curNodes;
        const victim = candidates[Math.floor(Math.random() * candidates.length)];
        setEdges((e) => e.filter(([a,b]) => a !== victim.id && b !== victim.id));
        return curNodes.filter((n) => n.id !== victim.id);
      });
    }, 13500);
    return () => clearInterval(itv);
  }, [reduceMotion]);

  /* metrics ticker */
  useEffect(() => {
    if (reduceMotion) return;
    const itv = setInterval(() => {
      setMetrics((m) => ({
        ...m,
        parsed: m.parsed + Math.floor(Math.random() * 4 + 1),
        rate: 138 + Math.floor(Math.random() * 9),
        nodes: m.nodes + (Math.random() < 0.5 ? 1 : 0),
        edges: m.edges + (Math.random() < 0.5 ? 1 : 0),
      }));
    }, 1400);
    return () => clearInterval(itv);
  }, [reduceMotion]);

  /* ambient flow packets along existing edges */
  const [ambientFlow, setAmbientFlow] = useState(null);
  useEffect(() => {
    if (reduceMotion) return;
    const itv = setInterval(() => {
      setEdges((curE) => {
        if (curE.length === 0) return curE;
        const edge = curE[Math.floor(Math.random() * curE.length)];
        setAmbientFlow({ id: uuid(), edge, born: Date.now() });
        return curE;
      });
    }, 4200);
    return () => clearInterval(itv);
  }, [reduceMotion]);

  /* ───────── RENDER ───────── */
  const nodeById = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);

  return (
    <div className="vd-ext-wrap" data-testid="vd-extraction-canvas" role="img"
         aria-label="Animated illustration: Luminex extracts structured processes from unstructured enterprise knowledge. Scattered documents, interview transcripts, Slack messages, and system logs drift from the left toward a central cognitive lens, where they are categorized as tasks, actors, gateways, artifacts, or constraints, and contribute to an evolving process graph on the right.">
      {/* top metadata strip */}
      <div className="vd-ext-topbar">
        <div className="vd-ext-metric vd-ext-metric-left">
          <span className="vd-ext-metric-icon">⬢</span>
          <div>
            <div className="vd-ext-metric-label">SOURCES</div>
            <div className="vd-ext-metric-val">{metrics.sources} · 32 formats</div>
          </div>
        </div>
        <div className="vd-ext-metric vd-ext-metric-mid">
          <span className="vd-ext-metric-icon">◈</span>
          <div>
            <div className="vd-ext-metric-label">EXTRACTION</div>
            <div className="vd-ext-metric-val">{metrics.rate}/sec · 97% conf.</div>
          </div>
        </div>
        <div className="vd-ext-metric vd-ext-metric-right">
          <span className="vd-ext-metric-icon">◇</span>
          <div>
            <div className="vd-ext-metric-label">PROCESS GRAPH</div>
            <div className="vd-ext-metric-val">{metrics.nodes.toLocaleString()} nodes · {metrics.edges.toLocaleString()} edges</div>
          </div>
        </div>
      </div>

      <svg viewBox="0 0 640 540" className={`vd-ext-svg ${reduceMotion ? "vd-ext-reduced" : ""}`}
           preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <defs>
          <linearGradient id="vd-lens-grad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%"   stopColor="#1E4E8C" stopOpacity="0" />
            <stop offset="50%"  stopColor="#1E4E8C" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#1E4E8C" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="vd-lens-grad-light" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%"   stopColor="#1E4E8C" stopOpacity="0" />
            <stop offset="50%"  stopColor="#1E4E8C" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#1E4E8C" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="vd-ember-glow">
            <stop offset="0%" stopColor="#F26B1F" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#F26B1F" stopOpacity="0" />
          </radialGradient>
          <filter id="vd-soft-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.4" />
          </filter>
          <marker id="vd-ext-arrow" markerWidth="5" markerHeight="4" refX="5" refY="2" orient="auto">
            <polygon points="0 0, 5 2, 0 4" className="vd-ext-arrow-fill" />
          </marker>
        </defs>

        {/* subtle zone dividers (optional hairlines) */}
        <line x1="225" y1="24" x2="225" y2="516" className="vd-ext-zone-div" />
        <line x1="355" y1="24" x2="355" y2="516" className="vd-ext-zone-div" />

        {/* ─── CHAOS ZONE — ambient particles ─── */}
        <g className="vd-ext-ambient">
          {AMBIENT_PARTICLES.map((p) => (
            <circle key={p.id} cx={p.x} cy={p.y} r="1.2"
                    className="vd-ext-particle"
                    style={{ animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s` }} />
          ))}
        </g>

        {/* ─── CHAOS ZONE — drifting fragments ─── */}
        <g className="vd-ext-fragments">
          {fragments.map((f) => (
            <Fragment key={f.id} frag={f} />
          ))}
        </g>

        {/* ─── COGNITIVE LENS ─── */}
        <g className="vd-ext-lens">
          <rect x="225" y="24" width="130" height="492" fill="url(#vd-lens-grad)" className="vd-ext-lens-col" />
          {/* lens frame hairlines */}
          <line x1="225" y1="24" x2="225" y2="516" className="vd-ext-lens-edge" />
          <line x1="355" y1="24" x2="355" y2="516" className="vd-ext-lens-edge" />
          {/* top label */}
          <text x="290" y="20" textAnchor="middle" className="vd-ext-lens-label">COGNITIVE LAYER</text>
          {/* scan line (CSS animation) */}
          {!reduceMotion && (
            <g className="vd-ext-scanline">
              <line x1="225" y1="0" x2="355" y2="0" className="vd-ext-scanline-l" />
              <line x1="225" y1="0" x2="355" y2="0" className="vd-ext-scanline-l2" />
            </g>
          )}
          {reduceMotion && (
            <line x1="225" y1="320" x2="355" y2="320" className="vd-ext-scanline-l" />
          )}
          {/* bottom parsing counter */}
          <text x="290" y="520" textAnchor="middle" className="vd-ext-lens-counter">
            {metrics.parsed.toLocaleString()} parsed · {metrics.patterns} patterns
          </text>
          {/* categorization token */}
          {catToken && (
            <g className="vd-ext-cat-token" key={catToken.id}>
              <circle cx={catToken.x - 4} cy={catToken.y} r="2.2" fill="#F26B1F" className="vd-ext-cat-spark" />
              <text x={catToken.x + 4} y={catToken.y + 3} className="vd-ext-cat-text">{catToken.label}</text>
            </g>
          )}
        </g>

        {/* ─── STRUCTURE ZONE — edges ─── */}
        <g className="vd-ext-edges">
          {edges.map(([a, b], i) => {
            const na = nodeById[a], nb = nodeById[b];
            if (!na || !nb) return null;
            const mx = (na.x + nb.x) / 2;
            const my = (na.y + nb.y) / 2 - 14;
            return (
              <path key={`${a}-${b}-${i}`}
                    d={`M${na.x},${na.y} Q${mx},${my} ${nb.x},${nb.y}`}
                    className="vd-ext-edge"
                    markerEnd="url(#vd-ext-arrow)" />
            );
          })}
          {/* ambient flow packet along a random edge */}
          {ambientFlow && (() => {
            const [a, b] = ambientFlow.edge;
            const na = nodeById[a], nb = nodeById[b];
            if (!na || !nb) return null;
            const mx = (na.x + nb.x) / 2;
            const my = (na.y + nb.y) / 2 - 14;
            return (
              <circle key={ambientFlow.id} r="2.4" className="vd-ext-flow-pkt">
                <animateMotion dur="1.6s" repeatCount="1" fill="freeze"
                               path={`M${na.x},${na.y} Q${mx},${my} ${nb.x},${nb.y}`} />
              </circle>
            );
          })()}
        </g>

        {/* ─── STRUCTURE ZONE — nodes ─── */}
        <g className="vd-ext-nodes">
          {nodes.map((n) => (
            <GraphNode key={n.id} node={n} pulsing={pulseNode === n.id} />
          ))}
        </g>

        {/* ─── DATA PACKETS (lens → target) ─── */}
        <g className="vd-ext-packets">
          {packets.map((p) => (
            <DataPacket key={p.id} pkt={p} />
          ))}
        </g>
      </svg>

      {/* bottom zone labels */}
      <div className="vd-ext-bottom">
        <div className="vd-ext-sep" />
        <div className="vd-ext-labels">
          <span className="vd-ext-label vd-ext-label-chaos">UNSTRUCTURED</span>
          <span className="vd-ext-label vd-ext-label-lens">EXTRACT</span>
          <span className="vd-ext-label vd-ext-label-struct">STRUCTURED</span>
        </div>
      </div>

      {/* screen-reader description */}
      <span className="sr-only">
        Illustration: fragments of enterprise knowledge — SOP snippets, interview quotes, Slack
        messages, emails, system logs, policies, meeting notes and tribal knowledge — drift from
        the left into a central cognitive lens where they are categorized as tasks, actors,
        gateways, artifacts or constraints, then flow as data packets into a living process
        graph on the right.
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   FRAGMENT COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
function Fragment({ frag }) {
  const trimmed = frag.text.length > 46 ? frag.text.slice(0, 44) + "…" : frag.text;
  const style = {
    animationDuration: `${frag.dur}s`,
    "--fy": `${frag.y}px`,
    "--fr": `${frag.rot}deg`,
  };
  return (
    <g className={`vd-ext-frag vd-ext-frag-${frag.type}`} style={style}>
      <rect x="0" y="-9" width="118" height="18" rx="3" className="vd-ext-frag-bg" />
      <FragmentGlyph type={frag.type} cx="7" cy="0" />
      <text x="14" y="3" className={`vd-ext-frag-text ${frag.type === "log" ? "vd-ext-frag-mono" : ""}`}>
        {trimmed}
      </text>
    </g>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   GRAPH NODE COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
function GraphNode({ node, pulsing }) {
  const cls = `vd-ext-node vd-ext-node-${node.kind} ${pulsing ? "vd-ext-node-pulse" : ""} ${node.justBorn ? "vd-ext-node-born" : ""}`;
  if (node.kind === "activity") {
    return (
      <g className={cls} transform={`translate(${node.x},${node.y})`}>
        <rect x="-45" y="-14" width="90" height="28" rx="7" className="vd-ext-node-bg" />
        <text x="0" y="4" textAnchor="middle" className="vd-ext-node-text">{node.label}</text>
      </g>
    );
  }
  if (node.kind === "actor") {
    return (
      <g className={cls} transform={`translate(${node.x},${node.y})`}>
        <circle r="16" className="vd-ext-node-bg" />
        <circle r="4.5" cx="0" cy="-3" className="vd-ext-actor-head" />
        <path d="M-7,7 Q0,0 7,7" className="vd-ext-actor-body" fill="none" />
        <text x="0" y="28" textAnchor="middle" className="vd-ext-node-label-sm">{node.label}</text>
      </g>
    );
  }
  if (node.kind === "gateway") {
    return (
      <g className={cls} transform={`translate(${node.x},${node.y}) rotate(45)`}>
        <rect x="-11" y="-11" width="22" height="22" className="vd-ext-node-bg" />
        <g transform="rotate(-45)">
          <text x="0" y="3.5" textAnchor="middle" className="vd-ext-node-gw">?</text>
        </g>
      </g>
    );
  }
  if (node.kind === "artifact") {
    const pts = [
      [0,-14],[12,-7],[12,7],[0,14],[-12,7],[-12,-7]
    ].map(p => p.join(",")).join(" ");
    return (
      <g className={cls} transform={`translate(${node.x},${node.y})`}>
        <polygon points={pts} className="vd-ext-node-bg vd-ext-artifact-bg" />
        <text x="0" y="3" textAnchor="middle" className="vd-ext-node-label-sm">{node.label}</text>
      </g>
    );
  }
  if (node.kind === "constraint") {
    const pts = [
      [-7,-11],[7,-11],[11,-7],[11,7],[7,11],[-7,11],[-11,7],[-11,-7]
    ].map(p => p.join(",")).join(" ");
    return (
      <g className={cls} transform={`translate(${node.x},${node.y})`}>
        <polygon points={pts} className="vd-ext-node-bg vd-ext-constraint-bg" />
        <text x="0" y="3" textAnchor="middle" className="vd-ext-node-ember">!</text>
        <text x="0" y="22" textAnchor="middle" className="vd-ext-node-label-ember">{node.label}</text>
      </g>
    );
  }
  return null;
}

/* ═══════════════════════════════════════════════════════════════════════
   DATA PACKET COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
function DataPacket({ pkt }) {
  const path = `M310,${pkt.fromY} Q${(310 + (pkt.targetX || 500)) / 2},${((pkt.fromY + (pkt.targetY || 200)) / 2) - 30} ${pkt.targetX || 500},${pkt.targetY || 200}`;
  return (
    <g className="vd-ext-pkt">
      <circle r="3" className="vd-ext-pkt-core">
        <animateMotion dur="1.2s" repeatCount="1" fill="freeze" path={path} />
      </circle>
      <circle r="5" className="vd-ext-pkt-halo">
        <animateMotion dur="1.2s" repeatCount="1" fill="freeze" path={path} />
      </circle>
    </g>
  );
}
